import axios, { AxiosProgressEvent, AxiosResponse } from 'axios'
import { apiNode } from './api'
import {
	FileUploadProps,
	CreateContentDataResponse,
	FolderDataActionProps,
	GetFileChatsDataResponse,
	GetPreviewDataResponse,
	GetContentProps,
	GetDocumentsDataResponse,
	GetRecentFilesResponse,
	GetDeletedDocumentsDataResponse,
	GetDocumentDataResponse,
	GetFolderDetailsDataResponse,
	GetSearchParamsProps,
	GetSearchData,
	GetThumbnailDataResponse,
	GetFileMetadataDataResponse,
	UpdateMetadataProps,
	TableDataDefaultGetParamsProps,
	CreateFileDataProps,
	MultiReclassifyObjectsDataProps,
	ShareFileDataProps,
	GetSharedPreviewDataResponse,
	GetSharedContentProps,
} from './types/document.interfaces'
import { DefaultGetParamsProps } from './types/interfaces'
import {
	buildFiltersParams,
	setOrderDirection,
} from '../utils/filters/filtersBuilder'

const URL_FILES = '2/files/v2/files'
const URL_FOLDERS = '2/folders/v2/folders'
const URL_FILE_SYSTEM = '2/file-system/v2'
const URL_SEARCH = '2/search'
const URL_TRASH = '/trash'

export const DocumentService = {
	async getFolderDetails({
		id,
		query,
		offset,
		orderBy,
		orderDirection,
		limit,
		columnFilters,
	}: TableDataDefaultGetParamsProps): Promise<GetFolderDetailsDataResponse> {
		const direction = setOrderDirection(orderDirection, orderBy)
		const response: AxiosResponse<GetFolderDetailsDataResponse> =
			await apiNode.get(`${URL_FOLDERS}/${id}`, {
				params: {
					query: query ? query : null,
					offset,
					limit,
					orderBy,
					orderDirection: direction,
					timezoneOffset: offset ? new Date().getTimezoneOffset() : null,
					...buildFiltersParams(columnFilters),
				},
			})
		return response.data
	},
	async getDocumentsByFolderId({
		id,
		query,
		offset,
		orderBy,
		orderDirection,
		limit,
		columnFilters,
	}: TableDataDefaultGetParamsProps): Promise<GetDocumentsDataResponse> {
		const isDateFilters = columnFilters?.some((f) =>
			['createdAt', 'modifiedAt']?.includes(f?.id),
		)
		const direction = setOrderDirection(orderDirection, orderBy)
		const response: AxiosResponse<GetDocumentsDataResponse> = await apiNode.get(
			`${URL_FOLDERS}/${id}/items`,
			{
				params: {
					query: query ? query : null,
					offset,
					limit,
					orderBy,
					orderDirection: direction,
					timezoneOffset: isDateFilters ? new Date().getTimezoneOffset() : null,
					...buildFiltersParams(columnFilters),
				},
			},
		)
		return response?.data
	},

	async getRecentDocuments() {
		const response: AxiosResponse<GetRecentFilesResponse> = await apiNode.get(
			`${URL_FILES}/recent`,
		)
		return response?.data
	},
	async getDeletedDocumentsByFolderId({
		id,
	}: DefaultGetParamsProps): Promise<GetDeletedDocumentsDataResponse> {
		const response: AxiosResponse<GetDeletedDocumentsDataResponse> =
			await apiNode.get(`${URL_FOLDERS}/${id}/items/deleted`)
		return response?.data
	},
	async createContent({
		size,
		storageKey,
		signal,
	}: {
		size?: number
		storageKey?: string
		signal?: AbortSignal
	}) {
		return apiNode.post<CreateContentDataResponse>(
			`${URL_FILES}/content`,
			{ size, storageKey },
			{
				signal,
			},
		)
	},
	async getContentById({ id }: DefaultGetParamsProps): Promise<ArrayBuffer> {
		const response: AxiosResponse<ArrayBuffer> = await apiNode.get<ArrayBuffer>(
			`${URL_FILES}/${id}/content`,
			{
				responseType: 'arraybuffer',
			},
		)
		return response.data
	},
	async getContentByVersionId({
		id,
		versionId,
	}: GetContentProps): Promise<ArrayBuffer> {
		const response: AxiosResponse<ArrayBuffer> = await apiNode.get<ArrayBuffer>(
			`${URL_FILES}/${id}/versions/${versionId}/content`,
			{
				responseType: 'arraybuffer',
			},
		)
		return response.data
	},
	async uploadToUrl({
		file,
		uploadUrl,
		onUploadProgress,
		signal,
	}: {
		file: File
		uploadUrl: string
		onUploadProgress?: (progressEvent: AxiosProgressEvent) => void
		signal?: AbortSignal
	}) {
		return axios.put(uploadUrl, file, {
			headers: {
				'Content-Type': 'application/octet-stream',
			},
			onUploadProgress,
			signal,
		})
	},
	async fileUpload({
		data,
		signal,
	}: {
		data: FileUploadProps
		signal?: AbortSignal
	}) {
		return apiNode.post(`${URL_FILES}/upload`, data, {
			signal,
		})
	},
	async createFolder({
		data,
		signal,
	}: {
		data: FolderDataActionProps
		signal?: AbortSignal
	}) {
		return apiNode.post(URL_FOLDERS, data, {
			signal,
		})
	},
	async createFile({
		data,
		signal,
	}: {
		data: CreateFileDataProps
		signal?: AbortSignal
	}) {
		return apiNode.post(URL_FILES, data, {
			signal,
		})
	},
	async getFileChat({
		id,
	}: DefaultGetParamsProps): Promise<GetFileChatsDataResponse> {
		const response: AxiosResponse<GetFileChatsDataResponse> = await apiNode.get(
			`${URL_FILES}/${id}/chats`,
			{},
		)
		return response.data
	},

	// -- File info
	async getPreview({
		id,
	}: DefaultGetParamsProps): Promise<GetPreviewDataResponse> {
		const response: AxiosResponse<GetPreviewDataResponse> =
			await apiNode.get<GetPreviewDataResponse>(`${URL_FILES}/${id}/preview`)
		return response.data
	},
	async getPreviewByVersion({
		id,
		versionId,
	}: GetContentProps): Promise<GetPreviewDataResponse> {
		const response: AxiosResponse<GetPreviewDataResponse> =
			await apiNode.get<GetPreviewDataResponse>(
				`${URL_FILES}/${id}/versions/${versionId}/preview`,
				{},
			)
		return response.data
	},
	async getThumbnail({
		id,
	}: DefaultGetParamsProps): Promise<GetThumbnailDataResponse> {
		const response: AxiosResponse<GetThumbnailDataResponse> =
			await apiNode.get<GetThumbnailDataResponse>(
				`${URL_FILES}/${id}/thumbnail`,
				{},
			)
		return response.data
	},
	async getThumbnailByVersion({
		id,
		versionId,
	}: GetContentProps): Promise<GetThumbnailDataResponse> {
		const response: AxiosResponse<GetThumbnailDataResponse> =
			await apiNode.get<GetThumbnailDataResponse>(
				`${URL_FILES}/${id}/versions/${versionId}/thumbnail`,
				{},
			)
		return response.data
	},
	async getMetadata({
		id,
	}: DefaultGetParamsProps): Promise<GetFileMetadataDataResponse> {
		const response: AxiosResponse<GetFileMetadataDataResponse> =
			await apiNode.get<GetFileMetadataDataResponse>(
				`${URL_FILES}/${id}/metadata`,
				{},
			)
		return response.data
	},
	async updateMetadata({ id, values }: UpdateMetadataProps) {
		return await apiNode.put(`${URL_FILES}/${id}/metadata`, values)
	},
	async multiClassifyObjects({
		data,
	}: {
		data: MultiReclassifyObjectsDataProps
	}) {
		return await apiNode.post(`${URL_FILE_SYSTEM}/metadata/classify`, data)
	},
	async classifyFile({ id }: DefaultGetParamsProps) {
		return await apiNode.post(`${URL_FILES}/${id}/metadata/classify`, {}, {})
	},
	async extractFileMetadata({ id }: DefaultGetParamsProps) {
		return await apiNode.post(`${URL_FILES}/${id}/metadata/extract`, {}, {})
	},
	async classifyFolder({ id }: DefaultGetParamsProps) {
		return await apiNode.post(`${URL_FOLDERS}/${id}/metadata/classify`, {}, {})
	},

	// -- Share file
	async shareFile({ id, data }: { id: string; data: ShareFileDataProps }) {
		return apiNode.post(`${URL_FILES}/${id}/share`, data)
	},

	// public
	async getSharedPreview(
		sharedToken: string,
	): Promise<GetSharedPreviewDataResponse> {
		const response: AxiosResponse<GetSharedPreviewDataResponse> =
			await apiNode.get<GetSharedPreviewDataResponse>(
				`${URL_FILES}/${sharedToken}/sharePreview`,
			)
		return response.data
	},

	async getSharedContent({
		sharedToken,
	}: Pick<GetSharedContentProps, 'sharedToken'>): Promise<ArrayBuffer> {
		const response: AxiosResponse<ArrayBuffer> = await apiNode.get<ArrayBuffer>(
			`${URL_FILES}/${sharedToken}/shareContent`,
			{
				responseType: 'arraybuffer',
			},
		)
		return response.data
	},

	// -- Object actions
	async getFileById({
		id,
	}: DefaultGetParamsProps): Promise<GetDocumentDataResponse> {
		const response: AxiosResponse<GetDocumentDataResponse> = await apiNode.get(
			`${URL_FILES}/${id}`,
			{},
		)
		return response?.data
	},
	async updateObjectName({
		name,
		type,
		id,
	}: {
		name: string
		type: number
		id: string
	}) {
		return await apiNode.patch(type ? URL_FOLDERS : URL_FILES, { name, id }, {})
	},
	async toggleObjectFavorite({
		value,
		type,
		id,
	}: {
		value: boolean
		type: number
		id: string
	}) {
		await apiNode.patch(
			`${type ? URL_FOLDERS : URL_FILES}/${id}/favorite`,
			{ value },
			{},
		)
	},
	async deleteObject({ type, id }: { type: number; id: string }) {
		await apiNode.delete(`${type ? URL_FOLDERS : URL_FILES}/${id}`)
	},
	async moveObject({
		data,
		parentId,
	}: {
		data: { id: string; name: string; type: number }
		parentId: string
	}) {
		const body: { parentId: string; name?: string } = { parentId }
		if (data?.name) {
			body.name = data.name
		}
		return apiNode.patch(
			`${data?.type ? URL_FOLDERS : URL_FILES}/${data?.id}/move`,
			body,
			{},
		)
	},
	async copyObject({
		data,
		parentId,
	}: {
		data: { id: string; name: string; type: number }
		parentId: string
	}) {
		const body: { parentId: string; name?: string } = { parentId }
		if (data?.name) {
			body.name = data.name
		}
		return apiNode.post(
			`${data?.type ? URL_FOLDERS : URL_FILES}/${data?.id}/copy`,
			body,
			{},
		)
	},
	async lockFile({ id, value }: { id: string; value: boolean }) {
		if (value) {
			return await apiNode.post(`${URL_FILES}/${id}/lock`, { z: 1 }, {})
		}
		return await apiNode.delete(`${URL_FILES}/${id}/lock`)
	},

	// file-system (several objects) actions
	async deleteObjects({
		data,
	}: {
		data: {
			type: number
			id: string
		}[]
	}) {
		return await apiNode.post(`${URL_FILE_SYSTEM}/delete`, data)
	},
	async moveObjects({
		data,
		parentId,
	}: {
		data: {
			id: string
			type: number
			name: string
		}[]
		parentId: string
	}) {
		return await apiNode.post(
			`${URL_FILE_SYSTEM}/move`,
			{ parentId, objects: data },
			{},
		)
	},
	async copyObjects({
		data,
		parentId,
	}: {
		data: {
			id: string
			type: number
			name: string
		}[]
		parentId: string
	}) {
		return await apiNode.post(
			`${URL_FILE_SYSTEM}/copy`,
			{ parentId, objects: data },
			{},
		)
	},
	async downloadObjects({ objects }: { objects: string[] }) {
		const response = await apiNode.post(
			`${URL_FILE_SYSTEM}/download`,
			objects,
			{},
		)
		return response?.data
	},
	async search({ ...params }: GetSearchParamsProps) {
		const response: AxiosResponse<GetSearchData> = await apiNode.get(
			`${URL_SEARCH}`,
			{
				params,
			},
		)
		return response?.data
		// return {
		// 	data: response?.data?.data?.entries,
		// 	success: response?.data?.success,
		// }
	},
	async searchAi({ ...params }: GetSearchParamsProps) {
		const response: AxiosResponse<GetSearchData> = await apiNode.get(
			`${URL_SEARCH}/ai`,
			{
				params,
			},
		)
		return response?.data
	},

	//favorites
	async getFavorites({
		query,
		offset,
		orderBy,
		orderDirection,
		limit,
		columnFilters,
	}: Omit<TableDataDefaultGetParamsProps, 'id'>) {
		const isDateFilters = columnFilters?.some((f) =>
			['createdAt', 'modifiedAt']?.includes(f?.id),
		)
		const direction = setOrderDirection(orderDirection, orderBy)
		const response: AxiosResponse<GetDocumentsDataResponse> = await apiNode.get(
			`${URL_FOLDERS}/favorite`,
			{
				params: {
					query: query ? query : null,
					offset,
					limit,
					orderBy,
					orderDirection: direction,
					timezoneOffset: isDateFilters ? new Date().getTimezoneOffset() : null,
					...buildFiltersParams(columnFilters),
				},
			},
		)
		return response?.data
	},

	//trash
	async getTrashDocuments({
		query,
		offset,
		orderBy,
		orderDirection,
		limit,
		columnFilters,
	}: Partial<TableDataDefaultGetParamsProps>): Promise<GetDeletedDocumentsDataResponse> {
		const direction = setOrderDirection(orderDirection, orderBy)

		const response: AxiosResponse<GetDeletedDocumentsDataResponse> =
			await apiNode.get(`${URL_TRASH}`, {
				params: {
					query: query ? query : null,
					offset,
					limit,
					orderBy,
					orderDirection: direction,
					timezoneOffset: offset ? new Date().getTimezoneOffset() : null,
					...buildFiltersParams(columnFilters),
				},
			})
		return response?.data
	},
	async deleteObjectFromTrash({ id }: { id: string }) {
		await apiNode.delete(`${URL_TRASH}/${id}`)
	},
	async restoreObjectFromTrash({ id }: { id: string }) {
		await apiNode.post(`${URL_TRASH}/${id}/restore`, { id })
	},
	async deleteObjectsFromTrash({ objects }: { objects: string[] }) {
		await apiNode.post(`${URL_TRASH}/clear`, { objects })
	},
	async restoreObjectsFromTrash({ objects }: { objects: string[] }) {
		await apiNode.post(`${URL_TRASH}/restore`, { objects })
	},
	async clearTrash() {
		await apiNode.post(`${URL_TRASH}/clear-all`, {})
	},
	// config versions
	async deleteObjectVersion({
		id,
		versionId,
	}: DefaultGetParamsProps & { versionId: string }) {
		await apiNode.delete(`${URL_FILES}/${id}/versions/${versionId}`)
	},
	async rollbackObjectVersion({
		id,
		versionId,
	}: DefaultGetParamsProps & { versionId: string }) {
		await apiNode.post(`${URL_FILES}/${id}/versions/${versionId}/rollback`, {
			id,
			versionId,
		})
	},

	async toggleObjectsFavorite({
		ids,
		value,
	}: {
		ids: string[]
		value: boolean
	}) {
		return await apiNode.patch(
			`${URL_FILE_SYSTEM}/favorite`,
			{ ids, value },
			{},
		)
	},
}
